import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import momentPlugin from '@fullcalendar/moment';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, Clock, Grid3X3 } from 'lucide-react';
import { Event } from '../types/Event';
import MonthYearPicker from './MonthYearPicker';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { toFullCalendarBusinessHours } from '../utils/businessHours';
import apiService from '../services/api';
import '@fullcalendar/core/locales/pt-br';
import '@fullcalendar/core/locales/es';
import '@fullcalendar/core/locales/it';
import '@fullcalendar/core/locales/fr';
import '@fullcalendar/core/locales/ja';
import '@fullcalendar/core/locales/ko';
import '@fullcalendar/core/locales/zh-cn';

// Import FullCalendar CSS from local files
import './fullcalendar-core.css';
import './fullcalendar-daygrid.css';
import './fullcalendar-timegrid.css';
import './FullCalendarComponent.css';

interface FullCalendarComponentProps {
  events: Event[];
  onEventClick: (event: Event) => void;
  onDateClick: (slotInfo: { start: Date; end: Date }) => void;
  onEventDrop: (eventInfo: { event: Event; start: Date; end: Date }) => void;
  onEventResize: (eventInfo: { event: Event; start: Date; end: Date }) => void;
}

const FullCalendarComponent: React.FC<FullCalendarComponentProps> = ({
  events,
  onEventClick,
  onDateClick,
  onEventDrop,
  onEventResize
}) => {
  const [view, setView] = useState<string>('timeGridWeek');
  const [isMobile, setIsMobile] = useState(false);
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [holidayBgEvents, setHolidayBgEvents] = useState<any[]>([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const calendarRef = useRef<FullCalendar>(null);
  const dateLabelRef = useRef<HTMLButtonElement>(null);
  const { t, i18n } = useTranslation();
  const { tenantSettings } = useAuth();
  // Shade slots outside the clinic's configured business hours. undefined when
  // no hours are set → FullCalendar shades nothing (legacy behaviour).
  const fcBusinessHours = useMemo(
    () => toFullCalendarBusinessHours(tenantSettings?.businessHours),
    [tenantSettings?.businessHours]
  );
  const fcLocale = (() => {
    const lang = (i18n.language || 'en').toLowerCase();
    if (lang.startsWith('pt')) return 'pt-br';
    if (lang.startsWith('es')) return 'es';
    if (lang.startsWith('it')) return 'it';
    if (lang.startsWith('fr')) return 'fr';
    if (lang.startsWith('ja')) return 'ja';
    if (lang.startsWith('ko')) return 'ko';
    if (lang.startsWith('zh')) return 'zh-cn';
    return 'en';
  })();

  // Force calendar refresh when events change
  useEffect(() => {
    if (calendarRef.current) {
      calendarRef.current.getApi().refetchEvents();
    }
  }, [events]);

  // Simple device detection
  useEffect(() => {
    const checkDevice = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkDevice();
    window.addEventListener('resize', checkDevice);
    return () => window.removeEventListener('resize', checkDevice);
  }, []);

  // Navigation handlers
  const handlePrev = useCallback(() => {
    if (calendarRef.current) {
      calendarRef.current.getApi().prev();
    }
  }, []);

  const handleNext = useCallback(() => {
    if (calendarRef.current) {
      calendarRef.current.getApi().next();
    }
  }, []);

  const handleToday = useCallback(() => {
    if (calendarRef.current) {
      calendarRef.current.getApi().today();
    }
  }, []);

  const handleViewChange = useCallback((newView: string) => {
    if (calendarRef.current) {
      calendarRef.current.getApi().changeView(newView);
      setView(newView);
    }
  }, []);

  // Convert events to FullCalendar format with enhanced color support
  const fullCalendarEvents = useMemo(() => events.map(event => {
    const timeStr = (!event.allDay && event.start) ? new Date(event.start).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }) : '';

    const nameAlreadyInTitle = event.clientName && event.title.includes(event.clientName);
    const eventTitle = event.allDay
      ? (event.clientName && !nameAlreadyInTitle ? `${event.title} (${event.clientName})` : event.title)
      : (event.clientName && !nameAlreadyInTitle ? `${timeStr} - ${event.title} (${event.clientName})` : `${timeStr} - ${event.title}`);
    
    // Calm "Sereno" event tones (mirror .fc-event.tone-* in index.css and the
    // design's data.jsx procedure palette). The CSS classes carry !important
    // background/text colors, so when a tone matches it fully styles the chip.
    const TONE_COLORS: { [key: string]: string } = {
      'tone-botox': '#4a6b58',
      'tone-filler': '#8a6d4a',
      'tone-consult': '#5c7481',
      'tone-followup': '#b8a883',
      'tone-peel': '#8d5d52',
      'tone-laser': '#3d4a5c',
    };
    const toneFor = (): string | null => {
      const s = `${event.title || ''} ${event.procedureName || ''}`.toLowerCase();
      if (/botox|toxina|botul/.test(s)) return 'tone-botox';
      if (/preench|filler|labial|malar|sculptra|bioestimul/.test(s)) return 'tone-filler';
      if (/consult|avalia/.test(s)) return 'tone-consult';
      if (/retorno|follow.?up|p[óo]s-?proced/.test(s)) return 'tone-followup';
      if (/peeling|peel/.test(s)) return 'tone-peel';
      if (/laser|co2/.test(s)) return 'tone-laser';
      return null;
    };

    // Respect an explicit per-event/procedure color (user data) untouched.
    // Otherwise pick a calm Sereno tone; fall back to a rotation of the same
    // muted tones rather than the old bright Google-style palette.
    let eventColor = event.color;
    let toneClass: string | null = null;
    if (!eventColor) {
      toneClass = toneFor();
      if (toneClass) {
        eventColor = TONE_COLORS[toneClass];
      } else if (event.clientName) {
        const clientHash = event.clientName.split('').reduce((a, b) => {
          a = ((a << 5) - a) + b.charCodeAt(0);
          return a & a;
        }, 0);
        const tones = Object.keys(TONE_COLORS);
        toneClass = tones[Math.abs(clientHash) % tones.length];
        eventColor = TONE_COLORS[toneClass];
      } else {
        toneClass = 'tone-consult';
        eventColor = TONE_COLORS[toneClass];
      }
    }

    // Patient-confirmation overlay (badge + fade), layered on top of the tone
    // color. confirmed → ✓, cancelled → ✕ (faded + struck), awaiting reply → ⏱.
    const confirmStateFor = (): 'confirmed' | 'cancelled' | 'pending' | null => {
      const cs = (event.confirmationStatus || '').toLowerCase();
      if (cs === 'confirmed') return 'confirmed';
      if (cs === 'cancelled') return 'cancelled';
      if (event.confirmationLinkSentAt && (cs === '' || cs === 'pending' || cs === 'no_response')) return 'pending';
      return null;
    };
    const confirmState = confirmStateFor();
    const baseClass = toneClass ? `fc-event-custom ${toneClass}` : 'fc-event-custom';
    const className = confirmState ? `${baseClass} confirm-${confirmState}` : baseClass;

    return {
      id: event.id,
      title: eventTitle,
      start: event.start,
      end: event.end,
      allDay: !!event.allDay,
      color: eventColor,
      backgroundColor: eventColor,
      borderColor: eventColor,
      textColor: '#ffffff',
      editable: !(event.isShared ?? false),
      className,
      extendedProps: {
        description: event.description,
        clientId: event.clientId,
        procedureId: event.procedureId,
        clientName: event.clientName,
        procedureName: event.procedureName,
        notes: event.notes,
        originalTitle: event.title,
        eventColor: eventColor,
        confirmState,
        isShared: event.isShared ?? false,
        resources: event.resources ?? []
      }
    };
  }), [events]);

  // Event handlers
  const handleEventClick = useCallback((clickInfo: any) => {
    // Prevent FullCalendar from leaving the event in a dragging state when
    // the click opens a modal (the modal intercepts focus so mouseup never fires).
    clickInfo.jsEvent.preventDefault();
    const event = events.find(e => e.id === clickInfo.event.id);
    if (event && !event.isShared) {
      onEventClick(event);
    }
  }, [events, onEventClick]);

  const handleDateClick = useCallback((clickInfo: any) => {
    const startTime = new Date(clickInfo.date);
    const endTime = new Date(startTime.getTime() + 30 * 60 * 1000);
    
    onDateClick({
      start: startTime,
      end: endTime
    });
  }, [onDateClick]);

  const handleSelect = useCallback((selectInfo: any) => {
    const startTime = new Date(selectInfo.start);
    const endTime = new Date(selectInfo.end);
    
    const duration = endTime.getTime() - startTime.getTime();
    if (duration >= 15 * 60 * 1000) {
      onDateClick({
        start: startTime,
        end: endTime
      });
    }
  }, [onDateClick]);

  const handleEventDrop = useCallback((dropInfo: any) => {
    const event = events.find(e => e.id === dropInfo.event.id);
    if (event && !event.isShared) {
      const newStart = new Date(dropInfo.event.start);
      const newEnd = new Date(dropInfo.event.end);
      
      // Haptic feedback for mobile
      if (isMobile && 'vibrate' in navigator) {
        navigator.vibrate(50);
      }
      
      onEventDrop({
        event,
        start: newStart,
        end: newEnd
      });
    }
  }, [events, onEventDrop, isMobile]);

  const handleEventResize = useCallback((resizeInfo: any) => {
    const event = events.find(e => e.id === resizeInfo.event.id);
    if (event && !event.isShared) {
      const newStart = new Date(resizeInfo.event.start);
      const newEnd = new Date(resizeInfo.event.end);
      
      // Haptic feedback for mobile
      if (isMobile && 'vibrate' in navigator) {
        navigator.vibrate(50);
      }
      
      onEventResize({
        event,
        start: newStart,
        end: newEnd
      });
    }
  }, [events, onEventResize, isMobile]);

  const handleViewChangeCallback = useCallback((viewInfo: any) => {
    setView(viewInfo.view.type);
  }, []);

  const handleDatesSet = useCallback((dateInfo: any) => {
    setCurrentDate(dateInfo.view.currentStart);
    const from = dateInfo.start.toISOString().slice(0, 10);
    const to = dateInfo.end.toISOString().slice(0, 10);
    apiService.get(`/api/booking-admin/overrides?from=${from}&to=${to}`).then((r: any) => {
      if (!r.success) return;
      const bgEvents = (r.data as any[])
        .filter((o: any) => o.type === 'block' && (o.userId == null || o.user_id == null))
        .map((o: any) => ({
          id: `holiday-${o.id}`,
          start: o.date,
          allDay: true,
          display: 'background',
          classNames: ['fc-holiday-block'],
          extendedProps: { note: o.note },
        }));
      setHolidayBgEvents(bgEvents);
    }).catch(() => {});
  }, []);

  // Get responsive configuration
  const getResponsiveConfig = () => {
    if (isMobile) {
      return {
        height: 'calc(100vh - 120px)',
        headerToolbar: false as const,
        buttonText: {
          today: t('calendar.today'),
          month: t('calendar.month'),
          week: t('calendar.week'),
          day: t('calendar.day')
        },
        dayMaxEvents: 3,
        moreLinkClick: 'popover',
        eventDisplay: 'block',
        slotMinTime: '06:00:00',
        slotMaxTime: '22:00:00',
        slotDuration: '00:30:00',
        snapDuration: '00:15:00',
        slotLabelInterval: '01:00:00',
        allDaySlot: true,
        nowIndicator: true,
        scrollTime: '08:00:00',
        scrollTimeReset: false,
        dayMaxEventRows: false,
        slotLabelFormat: {
          hour: '2-digit' as const,
          minute: '2-digit' as const,
          hour12: false
        },
        displayEventTime: false,
        eventTimeFormat: {
          hour: '2-digit' as const,
          minute: '2-digit' as const,
          hour12: false
        },
        // Mobile drag settings
        dragRevertDuration: 200,
        dragScroll: true,
        longPressDelay: 400, // Longer delay for mobile
        eventLongPressDelay: 400,
        selectLongPressDelay: 400,
        eventStartEditable: true,
        eventDurationEditable: true,
        // Mobile-optimized views
        views: {
          dayGridMonth: {
            dayHeaderFormat: { weekday: 'short' as const },
            dayMaxEvents: 3,
            moreLinkClick: 'popover'
          },
          timeGridWeek: {
            slotDuration: '00:30:00',
            snapDuration: '00:15:00',
            allDaySlot: true,
            dayHeaderFormat: { weekday: 'short' as const, day: 'numeric' as const },
            slotLabelFormat: {
              hour: '2-digit' as const,
              minute: '2-digit' as const,
              hour12: false
            }
          },
          timeGridDay: {
            slotDuration: '00:15:00',
            snapDuration: '00:15:00',
            allDaySlot: true,
            dayHeaderFormat: { weekday: 'short' as const, day: 'numeric' as const },
            slotLabelFormat: {
              hour: '2-digit' as const,
              minute: '2-digit' as const,
              hour12: false
            }
          }
        }
      };
    } else {
      return {
        height: 'calc(100vh - 140px)',
        headerToolbar: false as const,
        buttonText: {
          today: t('calendar.today'),
          month: t('calendar.month'),
          week: t('calendar.week'),
          day: t('calendar.day')
        },
        dayMaxEvents: 4,
        moreLinkClick: 'popover',
        eventDisplay: 'block',
        slotMinTime: '06:00:00',
        slotMaxTime: '22:00:00',
        slotDuration: '00:15:00',
        snapDuration: '00:15:00',
        slotLabelInterval: '01:00:00',
        allDaySlot: true,
        nowIndicator: true,
        scrollTime: '08:00:00',
        scrollTimeReset: false,
        dayMaxEventRows: false,
        slotLabelFormat: {
          hour: '2-digit' as const,
          minute: '2-digit' as const,
          hour12: false
        },
        displayEventTime: false,
        eventTimeFormat: {
          hour: '2-digit' as const,
          minute: '2-digit' as const,
          hour12: false
        },
        // Desktop drag settings
        dragRevertDuration: 200,
        dragScroll: true,
        longPressDelay: 200,
        eventLongPressDelay: 200,
        selectLongPressDelay: 200,
        eventStartEditable: true,
        eventDurationEditable: true,
        views: {
          dayGridMonth: {
            dayHeaderFormat: { weekday: 'short' as const },
            dayMaxEvents: 3,
            moreLinkClick: 'popover'
          },
          timeGridWeek: {
            slotDuration: '00:15:00',
            snapDuration: '00:15:00',
            allDaySlot: true,
            dayHeaderFormat: { weekday: 'short' as const, day: 'numeric' as const },
            slotLabelFormat: {
              hour: '2-digit' as const,
              minute: '2-digit' as const,
              hour12: false
            }
          },
          timeGridDay: {
            slotDuration: '00:15:00',
            snapDuration: '00:15:00',
            allDaySlot: true,
            dayHeaderFormat: { weekday: 'short' as const, day: 'numeric' as const },
            slotLabelFormat: {
              hour: '2-digit' as const,
              minute: '2-digit' as const,
              hour12: false
            }
          }
        }
      };
    }
  };

  const responsiveConfig = getResponsiveConfig();

  return (
    <div className="fullcalendar-component">
      {/* Custom Toolbar */}
      <div className="calendar-toolbar">
        <div className="toolbar-left">
          <button 
            className="toolbar-btn"
            onClick={handleToday}
          >
            <CalendarIcon size={16} />
            {t('calendar.today')}
          </button>
          
          <div className="view-buttons">
            {isMobile ? (
              <>
                <button
                  className={`view-btn ${view === 'timeGridWeek' ? 'active' : ''}`}
                  onClick={() => handleViewChange('timeGridWeek')}
                >
                  <CalendarIcon size={16} />
                  {t('calendar.week')}
                </button>
                <button
                  className={`view-btn ${view === 'timeGridDay' ? 'active' : ''}`}
                  onClick={() => handleViewChange('timeGridDay')}
                >
                  <Clock size={16} />
                  {t('calendar.day')}
                </button>
              </>
            ) : (
              <>
                <button
                  className={`view-btn ${view === 'dayGridMonth' ? 'active' : ''}`}
                  onClick={() => handleViewChange('dayGridMonth')}
                >
                  <Grid3X3 size={16} />
                  {t('calendar.month')}
                </button>
                <button
                  className={`view-btn ${view === 'timeGridWeek' ? 'active' : ''}`}
                  onClick={() => handleViewChange('timeGridWeek')}
                >
                  <CalendarIcon size={16} />
                  {t('calendar.week')}
                </button>
                <button
                  className={`view-btn ${view === 'timeGridDay' ? 'active' : ''}`}
                  onClick={() => handleViewChange('timeGridDay')}
                >
                  <Clock size={16} />
                  {t('calendar.day')}
                </button>
              </>
            )}
          </div>
        </div>

        <div className="toolbar-center">
          <button
            className="nav-btn"
            onClick={handlePrev}
          >
            <ChevronLeft size={20} />
          </button>

          <button
            type="button"
            ref={dateLabelRef}
            className="current-date"
            onClick={() => setPickerOpen(o => !o)}
            aria-haspopup="dialog"
            aria-expanded={pickerOpen}
            aria-label={t('calendar.jumpToDate')}
          >
            {currentDate.toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric'
            })}
          </button>

          <button
            className="nav-btn"
            onClick={handleNext}
          >
            <ChevronRight size={20} />
          </button>
        </div>
      </div>

      <MonthYearPicker
        isOpen={pickerOpen}
        anchorEl={dateLabelRef.current}
        year={currentDate.getFullYear()}
        month={currentDate.getMonth()}
        locale={fcLocale}
        onSelect={(year, month) => {
          calendarRef.current?.getApi().gotoDate(new Date(year, month, 1));
          setPickerOpen(false);
        }}
        onClose={() => setPickerOpen(false)}
      />

      {/* Calendar */}
      <div className="calendar-container">
        <FullCalendar
          ref={calendarRef}
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, momentPlugin]}
          initialView={isMobile ? 'timeGridWeek' : 'timeGridWeek'}
          events={[...fullCalendarEvents, ...holidayBgEvents]}
          eventClick={handleEventClick}
          dateClick={handleDateClick}
          select={handleSelect}
          eventDrop={handleEventDrop}
          eventResize={handleEventResize}
          viewDidMount={handleViewChangeCallback}
          datesSet={handleDatesSet}
          eventDidMount={(info) => {
            info.el.setAttribute('data-testid', `event-${info.event.id}`);
            
            // Get color from extendedProps first, then fallback to event properties
            const chosen = info.event.extendedProps?.eventColor ||
                          info.event.backgroundColor ||
                          (info.event as any).color ||
                          '#1a73e8';

            const isShared = info.event.extendedProps?.isShared === true;

            if (isShared) {
              // Ghost style for shared colleague events: transparent fill, solid border
              const hex = chosen.replace('#', '');
              const r = parseInt(hex.slice(0, 2), 16) || 0;
              const g = parseInt(hex.slice(2, 4), 16) || 0;
              const b = parseInt(hex.slice(4, 6), 16) || 0;
              info.el.style.backgroundColor = `rgba(${r}, ${g}, ${b}, 0.15)`;
              info.el.style.borderColor = chosen;
              info.el.style.borderStyle = 'solid';
              info.el.style.borderWidth = '2px';
              info.el.style.color = chosen;
              // Override FullCalendar's white text on inner elements
              info.el.style.setProperty('--fc-event-text-color', chosen);
              info.el.querySelectorAll('.fc-event-title, .fc-event-time, .fc-event-main').forEach((el) => {
                (el as HTMLElement).style.color = chosen;
              });
            } else {
              // Apply the color
              info.el.style.backgroundColor = chosen;
              info.el.style.borderColor = chosen;
              info.el.style.borderStyle = 'solid';
              info.el.style.borderWidth = '2px';
              info.el.style.color = '#ffffff';
            }
            info.el.setAttribute('data-event-color', chosen);

            // Patient-confirmation badge (✓ confirmed / ✕ cancelled / ⏱ awaiting).
            // The fade + strikethrough for cancelled come from the confirm-* CSS
            // class set on the event; here we just inject the corner glyph.
            const confirmState = info.event.extendedProps?.confirmState as
              ('confirmed' | 'cancelled' | 'pending' | undefined);
            if (confirmState && !info.el.querySelector('.fc-confirm-badge')) {
              const GLYPH = { confirmed: '✓', cancelled: '✕', pending: '⏱' } as const;
              const LABEL = {
                confirmed: t('calendar.confirmation.confirmed'),
                cancelled: t('calendar.confirmation.cancelled'),
                pending: t('calendar.confirmation.pending'),
              } as const;
              const badge = document.createElement('span');
              badge.className = `fc-confirm-badge confirm-${confirmState}`;
              badge.textContent = GLYPH[confirmState];
              badge.title = LABEL[confirmState];
              badge.setAttribute('aria-label', LABEL[confirmState]);
              info.el.appendChild(badge);
            }

            // Resource (sala) chips: a small colored dot per booked room, with the
            // room name on hover. Mirrors the confirm-badge injection above.
            const resources = (info.event.extendedProps?.resources || []) as { id: number; name: string; color: string }[];
            if (resources.length > 0 && !info.el.querySelector('.fc-resource-chips')) {
              const wrap = document.createElement('span');
              wrap.className = 'fc-resource-chips';
              wrap.style.cssText = 'display:inline-flex;gap:4px;align-items:center;margin-left:4px;vertical-align:middle;max-width:100%;overflow:hidden';
              wrap.title = resources.map(r => r.name).join(', ');
              resources.forEach((r) => {
                // Colored dot + room name so the room is readable at a glance,
                // not only on hover. Name truncates on narrow columns.
                const chip = document.createElement('span');
                chip.style.cssText = 'display:inline-flex;gap:3px;align-items:center;min-width:0;flex-shrink:1';
                const dot = document.createElement('span');
                dot.style.cssText = `width:7px;height:7px;border-radius:2px;background:${r.color || '#6366f1'};box-shadow:0 0 0 1px rgba(255,255,255,.6);flex-shrink:0`;
                const name = document.createElement('span');
                name.textContent = r.name;
                name.style.cssText = 'font-size:.82em;opacity:.85;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;min-width:0';
                chip.appendChild(dot);
                chip.appendChild(name);
                wrap.appendChild(chip);
              });
              const titleEl = info.el.querySelector('.fc-event-title');
              if (titleEl) titleEl.appendChild(wrap); else info.el.appendChild(wrap);
            }

            // Mobile touch handling
            if (isMobile) {
              info.el.style.touchAction = 'none';
              info.el.style.userSelect = 'none';
              info.el.style.webkitUserSelect = 'none';
              info.el.classList.add('fc-event-mobile-draggable');
            }
          }}
          editable={true}
          droppable={true}
          selectable={true}
          selectMirror={true}
          locale={fcLocale}
          businessHours={fcBusinessHours ?? false}
          {...responsiveConfig}
        />
      </div>

      {/* Mobile Instructions */}
      {isMobile && (
        <div className="mobile-instructions">
          <span>👆 Tap to create • 🖐️ Swipe to navigate • 👆 Hold to drag</span>
        </div>
      )}
    </div>
  );
};

export default React.memo(FullCalendarComponent);
